import { useEffect, useState } from 'react'
import { Avatar } from '../../components/Avatar'
import { readError } from '../../lib/errors'
import { useT } from '../../i18n'
import {
  fetchWordWeeklyChart, type WordChartRow,
} from '../../lib/api/wordGames'

interface Props {
  game: 'fake_out' | 'close_enough'
  /** Bump to refetch after the player scores. */
  refresh?: number
  accentClass: string
}

/**
 * This week's chart: the server returns the top ten plus the caller's
 * own row. When the caller sits outside the top ten, their row takes
 * the tenth slot, wearing their real rank number (owner rule).
 */
export function WordWeeklyChart({ game, refresh = 0, accentClass }: Props) {
  const t = useT()
  const [rows, setRows] = useState<WordChartRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    fetchWordWeeklyChart(game)
      .then(r => { if (!cancelled) setRows(r) })
      .catch(e => { if (!cancelled) setError(readError(e)) })
    return () => { cancelled = true }
  }, [game, refresh])

  const display = (() => {
    if (!rows) return null
    const top = rows.filter(r => r.rank <= 10)
    const me = rows.find(r => r.is_me)
    if (me && me.rank > 10) return [...top.slice(0, 9), me]
    return top
  })()

  return (
    <div className="mt-6">
      <p className="text-[10px] font-semibold uppercase tracking-widest text-brand-muted mb-2">
        {t("This week's chart")}
      </p>
      {error && <p className="text-xs text-brand-danger">{error}</p>}
      {display?.length === 0 && (
        <p className="text-sm text-brand-muted">
          {t('No scores yet this week. Yours starts the race.')}
        </p>
      )}
      <div className="space-y-2">
        {display?.map(r => (
          <div key={r.profile_id}
               className={`flex items-center gap-3 bg-white border rounded-2xl px-3 py-2
                           ${r.is_me ? 'border-brand-text' : 'border-brand-border'}`}>
            <span className={`w-7 text-center font-display font-bold ${r.rank === 1 ? accentClass : 'text-brand-muted'}`}>
              {r.rank}
            </span>
            <Avatar name={r.display_name} avatarUrl={r.avatar_url} size="sm" />
            <span className="flex-1 min-w-0 truncate text-sm font-semibold text-brand-text">
              {r.display_name}
            </span>
            <span className="text-xs text-brand-muted">
              {r.days_played}d
            </span>
            <span className="font-display font-bold text-brand-text">
              {r.total}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}
